import * as coreEntities from 'core/store/index';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import analyticsService from '../../services/analyticsService';
import { createHeaders, getApiUrls } from '../../services/serviceConfig';
import { transformApiMessagesToUI } from '../../utils/messageTransformer';
import type { AnnotationStatus, FlaggedMessage } from '../TeacherAIAnalyticsPanelView/sections/InappropriateUseSection/useInappropriateUseSection';

type MessageDto = {
  attachments: Array<{
    contentType: string;
    createdAt: string;
    fileName: string;
    sizeInBytes: number;
    url: string;
  }>;
  content: string;
  createdAt: string;
  id: string;
  isFlagged: boolean;
  participant: 'user' | 'assistant' | 'system';
};

type StudentConversation = {
  createdAt: string;
  id: string;
  messages: MessageDto[];
  mode: string;
  status: 'ACTIVE' | 'ARCHIVED';
  title: string;
  updatedAt: string;
};

type UIMessage = {
  attachments: Array<{
    contentType: string;
    createdAt: string;
    fileName: string;
    sizeInBytes: number;
    url: string;
  }>;
  context: Record<string, unknown>;
  id: string;
  isFlagged: boolean;
  sender: 'user' | 'bot';
  text: string;
  timestamp: Date;
};

type UseStudentConversationDrawerProps = {
  conversationId: string | null;
  message: FlaggedMessage | null;
  onStatusChanged?: (annotationId: string, newStatus: AnnotationStatus, reviewerName: string) => void;
  open: boolean;
};

type UseStudentConversationDrawerReturn = {
  conversation: UIMessage[];
  error: string | null;
  handleDismiss: () => void;
  handleMarkAsAttended: () => void;
  loading: boolean;
  updating: boolean;
};

function useStudentConversationDrawer({
  conversationId,
  message,
  onStatusChanged,
  open,
}: UseStudentConversationDrawerProps): UseStudentConversationDrawerReturn {
  const currentUser = useSelector((state: any) => coreEntities.auth.selectors.getUser(state));
  const [conversation, setConversation] = useState<UIMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);

  const fetchConversation = useCallback(async () => {
    if (!conversationId) return;

    setLoading(true);
    setError(null);

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.teacherReview.conversation(conversationId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get student conversation: ${response.status}`);
      }

      const data: StudentConversation = await response.json();
      const uiMessages = transformApiMessagesToUI(data.messages) as UIMessage[];
      setConversation(uiMessages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setConversation([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (open && conversationId) {
      fetchConversation();
    }

    if (!open) {
      setConversation([]);
      setError(null);
    }
  }, [open, conversationId, fetchConversation]);

  const getCurrentUserName = useCallback((): string => {
    if (!currentUser) return '';
    return `${currentUser.name || ''} ${currentUser.lastname || ''}`.trim();
  }, [currentUser]);

  const handleMarkAsAttended = useCallback(async () => {
    if (!message?.annotationId) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(message.annotationId, { status: 'REVIEWED' });
      onStatusChanged?.(message.annotationId, 'REVIEWED', getCurrentUserName());
    } catch (err) {
      console.error('Failed to mark as attended:', err);
    } finally {
      setUpdating(false);
    }
  }, [message?.annotationId, onStatusChanged, getCurrentUserName]);

  const handleDismiss = useCallback(async () => {
    if (!message?.annotationId) return;

    setUpdating(true);
    try {
      await analyticsService.updateAnnotationStatus(message.annotationId, { status: 'DISMISSED' });
      onStatusChanged?.(message.annotationId, 'DISMISSED', getCurrentUserName());
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setUpdating(false);
    }
  }, [message?.annotationId, onStatusChanged, getCurrentUserName]);

  return {
    conversation,
    error,
    handleDismiss,
    handleMarkAsAttended,
    loading,
    updating,
  };
}

export default useStudentConversationDrawer;
